import {Fab, FormControlLabel, Grid, Switch, Tooltip} from '@material-ui/core';
import {green, red} from '@material-ui/core/colors';
import {
  Add,
  ArrowDownward,
  Cancel,
  Check,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clear,
  Close,
  Edit,
  FilterList,
  FirstPage,
  Info,
  LastPage,
  PersonAdd,
  Search
} from '@material-ui/icons';
import MaterialTable, {MTableToolbar} from 'material-table';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {connect} from 'react-redux';
import locale from '../locale/Localization';
import {checkRolePermissions} from '../permissions/permissions';
import {getPersonsByCanton} from '../script/person-service.js';
import {
  NOTARY_FUNCTION_STATUS_ACTIVE,
  NOTARY_FUNCTION_STATUS_INACTIVE
} from '../utils/constants';

const tableIcons = {
  Search: Search,
  Filter: FilterList,
  PreviousPage: ChevronLeft,
  NextPage: ChevronRight,
  FirstPage: FirstPage,
  LastPage: LastPage,
  Add: Add,
  Edit: Edit,
  Check: Check,
  Close: Close,
  SortArrow: ArrowDownward,
  ResetSearch: Clear
};

const tableLocalization = {
  toolbar: {
    searchTooltip: locale.tableSearchTooltip,
    searchPlaceholder: locale.tableSearchPlaceholder
  },
  header: {
    actions: locale.tableHeaderActions
  },
  body: {
    emptyDataSourceMessage: locale.tableEmptyDataSourceMessage
  },
  pagination: {
    labelRowsPerPage: locale.tableLabelRowsPerPage,
    labelDisplayedRows: locale.tableLabelDisplayedRows,
    labelRowsSelect: locale.tableLabelRowsSelect,
    firstTooltip: locale.tableFirstTooltip,
    previousTooltip: locale.tablePreviousTooltip,
    nextTooltip: locale.tableNextTooltip,
    lastTooltip: locale.tableLastTooltip
  }
};

const styles = {
  root: {marginTop: '3%'},
  toolbar: {padding: '10px 14px'}
};

/**
 * Map the state to the properties.
 *
 * @param {Object} state - The state from store.
 * @return {Object} - The state.
 */
const mapStateToProps = (state: Object) => {
  return {
    user: state.user,
    roles: state.roles
  };
};

/**
 * Connected component class of NotariesList.
 */
class ConnectedNotariesList extends Component {
  // eslint-disable-next-line require-jsdoc
  constructor(props) {
    super(props);
    this.state = {
      notariesData: [],
      notaryDetailsClicked: true,
      seletedRowData: null,
      showInactive: false
    };
  }

  handleActiveInactiveToggle = () => {
    this.setState({showInactive: !this.state.showInactive});
  };
  /**
   * Fetch list of notaries.
   */
  fetchNotaries = () => {
    getPersonsByCanton('VD').then((result) => {
      const notaries = [];
      result.forEach(function (item) {
        const organization = item.organisation || {};

        notaries.push({
          id: item.id,
          lastname: item.officialName,
          firstName: item.firstNames,
          organisation: organization.name,
          status: item.status,
          validFrom: item.validFrom,
          validTo: item.validTo
        });
      });
      this.setState({notariesData: notaries});
    });
  };

  componentDidMount = () => {
    this.fetchNotaries();
  };

  /**
   * Function to display the document upload.
   */
  displayNotaryAdd = () => {
    this.setState({
      open: true
    });
    this.props.onNotaryAddShown();
  };

  /**
   * Renders add notary button.
   *
   * @return {string} - The button to be rendered.
   */
  renderNotaryAddButton = () => {
    return (
      <React.Fragment>
        <Tooltip title={locale.addNotaryButton} placement='top'>
          <Fab
            size='small'
            color='default'
            onClick={() => this.displayNotaryAdd()}
          >
            <PersonAdd />
          </Fab>
        </Tooltip>
      </React.Fragment>
    );
  };

  /**
   * Renders NotaryList component.
   *
   * @return {string} - The notary list component to be rendered.
   */
  // eslint-disable-next-line max-lines-per-function, require-jsdoc
  render() {
    return (
      <div style={styles.root}>
        <MaterialTable
          icons={tableIcons}
          title={locale.notariesListTitle}
          localization={tableLocalization}
          columns={[
            {title: 'id', field: 'id', hidden: true},
            {
              title: locale.name,
              field: 'lastname',
              defaultSort: 'asc',
              customSort: (a, b) => a.lastname.localeCompare(b.lastname)
            },
            {
              title: locale.firstName,
              field: 'firstName',
              customSort: (a, b) => a.firstName.localeCompare(b.firstName)
            },
            {
              title: locale.organisation,
              field: 'organisation',
              customSort: (a, b) => a.organisation.localeCompare(b.organisation)
            },
            {
              title: locale.status,
              field: 'status',
              sorting: false,
              // eslint-disable-next-line react/display-name
              render: (rowData) =>
                rowData.status === NOTARY_FUNCTION_STATUS_ACTIVE ? (
                  <Tooltip title={locale.statusActive} placement='bottom'>
                    <CheckCircle style={{color: green[500]}} />
                  </Tooltip>
                ) : (
                  <Tooltip title={locale.statusInactive} placement='bottom'>
                    <Cancel style={{color: red[500]}} />
                  </Tooltip>
                )
            },
            {
              title: locale.from,
              field: 'validFrom',
              render: (rowData) =>
                rowData.validFrom &&
                moment(rowData.validFrom).format(locale.localizedDateFormat)
            },
            {
              title: locale.to,
              field: 'validTo',
              render: (rowData) =>
                rowData.validTo &&
                moment(rowData.validTo).format(locale.localizedDateFormat)
            }
          ]}
          data={this.state.notariesData.filter(
            (notary) =>
              (this.state.showInactive &&
                notary.status === NOTARY_FUNCTION_STATUS_INACTIVE) ||
              (!this.state.showInactive &&
                notary.status === NOTARY_FUNCTION_STATUS_ACTIVE)
          )}
          options={{
            sorting: true,
            actionsColumnIndex: -1,
            showTitle: true,
            search: true
          }}
          components={{
            Toolbar: (props) => (
              <div>
                <MTableToolbar {...props} />
                <Grid container item xs={12} spacing={3} style={styles.toolbar}>
                  <Grid item xs={11}>
                    <FormControlLabel
                      control={
                        <Switch
                          size='small'
                          checked={this.state.showInactive}
                          onChange={() => {
                            this.handleActiveInactiveToggle();
                          }}
                        />
                      }
                      label={locale.showInactive}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    {checkRolePermissions(
                      this.props.roles,
                      'VIEW',
                      'Applications'
                    ) && this.renderNotaryAddButton()}
                  </Grid>
                </Grid>
              </div>
            )
          }}
          actions={[
            {
              icon: Info,
              tooltip: locale.details,
              onClick: (event, rowData) => {
                {
                  this.setState({
                    open: true,
                    selectedRowData: rowData
                  });
                  this.props.onNotaryDetailsShown(rowData);
                }
              }
            },
            {
              icon: Edit,
              tooltip: locale.editNotary,
              onClick: (event, rowData) => {
                {
                  this.setState({
                    open: true,
                    selectedRowData: rowData
                  });
                  this.props.onNotaryEditShown(rowData);
                }
              },
              hidden: !checkRolePermissions(
                this.props.roles,
                'VIEW',
                'Applications'
              )
            }
          ]}
        />
      </div>
    );
  }
}

ConnectedNotariesList.propTypes = {
  onNotaryAddShown: PropTypes.func,
  onNotaryDetailsShown: PropTypes.func,
  onNotaryEditShown: PropTypes.func,
  roles: PropTypes.array.isRequired,
  user: PropTypes.object.isRequired
};

const NotariesList = connect(mapStateToProps, null)(ConnectedNotariesList);
export default NotariesList;
